import { DashboardEventDetails } from 'v2/components/dashboard/DashboardEventDetails/DashboardEventDetails';
import TeamSchedule from 'v2/components/TeamSchedule';
import {
  useDashboardFilters,
  PageOptions
} from '@/contexts/DashboardStateContext';
import { goToMap } from '@/lib/utils-map';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { AgendaWeek } from '@webV2/components/dashboard/AgendaWeek';
import { useDashboardLocation } from '@/hooks/useDashboardLocation';
import { cn } from '@v2/lib/utils';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent
} from '@v2/components/ui/sidebar';
import { WeekSwitch } from '@cmp-src/components/calendar/WeekSwitch';
import { OOORequest } from '@/components/attendance/employee/OOORequest';
import { ConfirmModal } from '@cmp-src/overlay/modals/ConfirmModal';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@v2/components/ui/tabs';
import { Visitors } from '@webV2/components/dashboard-subpages/Visitors';
import { News } from '@webV2/components/dashboard-subpages/News';
import { SiteInfo } from '@webV2/components/dashboard-subpages/SiteInfo';
import { DashboardContentWrapper } from '@cmp-src/components/dashboard/DashboardContentWrapper';
import { canScheduleOthers } from '@/lib/permissions';
import { useAuthenticated, usePageSectionsQuery, useHasStations } from '@gettactic/hooks';
import { SelectOffices } from '@webV2/components/SelectOffices';
import { useTeamAttendanceFilters } from '@webV2/components/TeamSchedule/hooks/useTeamAttendanceFilters';
import {
  ColumnWidthProvider,
  calculateTotalWidth
} from '@gettactic/components/src/contexts/ColumnWidthContext';
import {
  CardV2LayoutClasses,
  MobileBottomCalcVh
} from '@v2/components/display/Card/classes';
import { useBreakpoint } from '@gettactic/helpers/src/hooks/useBreakpoint';
import { Switch } from '@v2/components/ui/Switch';
import { ArrowLeftIcon, CalendarRange } from 'lucide-react';
import { Button } from '@v2/components/ui/button';
import { CloseIcon } from '@v2/icons';

const tabToPageOption: Record<string, PageOptions> = {
  attendance: 'Attendance',
  visitors: 'Visitors',
  news: 'News',
  siteInfo: 'SiteInfo'
};

const pageOptionToTab: Record<PageOptions, string> = {
  Attendance: 'attendance',
  Visitors: 'visitors',
  News: 'news',
  SiteInfo: 'siteInfo'
};

const DEFAULT_DAYS_COUNT = 5;

export default function V2Dashboard() {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const { hasStations } = useHasStations();
  const { isBreakpoint } = useBreakpoint();

  const isMd = isBreakpoint('md');

  const {
    selectedEvent,
    setWeekDate: setDashboardWeekDate,
    page,
    setPage,
    filters,
    setSelectedEvent,
    agendaDisclosure
  } = useDashboardFilters();

  useEffect(() => {
    if (selectedEvent) {
      setPage(tabToPageOption.attendance);
    }
  }, [selectedEvent]);

  const router = useRouter();
  const [shouldRenderContent, setShouldRenderContent] = useState(false);

  const activeTab = pageOptionToTab[page || tabToPageOption.attendance];

  // Create a ref for measuring container width
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    filters: dashboardFilters,
    isComponentLoading,
    defaultOffice: _defaultOffice,
    setOfficesId: setOffices
  } = useTeamAttendanceFilters({
    authenticatedUser,
    currentOffice,
    onlyFilters: true
  });

  const defaultOffice = useRef(_defaultOffice);
  useEffect(() => {
    if (_defaultOffice && !defaultOffice.current) {
      defaultOffice.current = _defaultOffice;
    }
  }, [_defaultOffice]);

  // Set initial tab from URL if available
  useEffect(() => {
    if (!router.isReady) return;

    if (
      router.query.dashboard_tab &&
      typeof router.query.dashboard_tab === 'string'
    ) {
    } else if (!page) {
      setPage(tabToPageOption.attendance);
    }
  }, [router.isReady]);

  // Always render both components, but hide one with CSS
  // This ensures the same hooks are called on every render
  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    // Check if we should redirect to the map
    // TODO: remove this once we have properly implemented map links
    // See: https://app.shortcut.com/tactic/story/2705/locate-button-doesn-t-filter-for-the-floor
    // See: https://tactic-hq.slack.com/archives/C07L5L6GLUF/p1737385471739359
    const [_, hash] = (router.asPath as string).split('#');
    const parsedHash = new URLSearchParams(hash);
    const resourceId = parsedHash.get('resource');
    const officeId = parsedHash.get('office');
    const floorId = parsedHash.get('floor');
    const date = parsedHash.get('date');
    const shouldGoToMap = parsedHash.get('goToMap');
    if (shouldGoToMap && resourceId && officeId && date) {
      goToMap(router, resourceId, officeId, date, floorId);
    }

    setShouldRenderContent(true);
  }, [router.isReady]);

  const locationData = useDashboardLocation({
    onDateChange: (date: string) => {
      setDashboardWeekDate(date);
    }
  });

  const isAdmin = canScheduleOthers(authenticatedUser);

  const { data: sections } = usePageSectionsQuery();
  const hasPages = sections?.length && sections?.length > 0;

  // let daysCount = locationData.availableDays?.length || 0;
  // if(!daysCount && (locationData.availableDays as any).elements) {
  //   daysCount = (locationData.availableDays as any).elements?.filter((day: IHourPost) => day.type === 'weekday').length || 0;
  // }

  // Ensure daysCount is calculated correctly even if availableDays is malformed
  // const safeDaysCount = Array.isArray(locationData.availableDays)
  //   ? locationData.availableDays.length
  //   : 0;
  // const finalDaysCount = safeDaysCount || daysCount || DEFAULT_DAYS_COUNT;
  // For now we're hardcoding the days count to 5 because Chris and Reid think it's better
  const finalDaysCount = DEFAULT_DAYS_COUNT;

  const dashboardWidth = calculateTotalWidth(finalDaysCount);

  const handleTabChange = (value: string) => {
    if (value in tabToPageOption) {
      setPage(tabToPageOption[value as keyof typeof tabToPageOption]);
    }
  };

  if (!shouldRenderContent) {
    return null; // Return null until router is ready to prevent hooks from being conditionally rendered
  }

  const SIDEBAR_WIDTH = 389;
  //const ICON_WIDTH = 68;
  const WHOLE_WIDTH = `${SIDEBAR_WIDTH}px`;

  const {
    nextWeek,
    prevWeek,
    weekDate,
    setWeekDate,
    scheduleMeData,
    setOooRequest,
    oooRequest,
    onConfirmDialogCallback,
    setOnConfirmDialogCallback
  } = locationData;

  const calendarSwitch = (
    <button
      className={'flex items-center gap-1 md:hidden'}
      onClick={() => {
        agendaDisclosure?.toggle();
      }}
    >
      <CalendarRange className={'size-4'} />
      <Switch
        checked={agendaDisclosure?.isOpen || false}
        variant={'squared'}
        size={'xs'}
      />
    </button>
  );

  // Always render both components, but hide one with CSS
  return (
    <>
      <div className={'relative h-full'}>
        <div className={cn('flex h-full flex-row')}>
          <div
            data-state={agendaDisclosure?.isOpen ? 'open' : 'closed'}
            className={cn(
              agendaDisclosure?.isOpen ? 'fixed' : 'hidden',
              'top-12 right-4 bottom-0 left-0 z-sticky flex-col bg-white md:static md:flex md:bg-transparent',
              !isMd &&
                'data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2/3 data-[state=open]:fade-in-0 data-[state=open]:animate-in',
              !isMd &&
                'data-[state=closed]:zoom-out-90 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out'
            )}
          >
            <div
              className="flex w-full items-center justify-between gap-2 pt-v2-layout-card-out pl-v2-layout-card-out text-sm"
              style={isMd ? { width: WHOLE_WIDTH } : undefined}
            >
              {calendarSwitch}
              <WeekSwitch
                prevWeek={prevWeek}
                nextWeek={nextWeek}
                setWeekDate={setWeekDate}
                weekDate={`${weekDate}T00:00:00.000Z`}
              />
            </div>
            <ScrollArea.Root
              type="hover"
              className={cn(
                'pt-6 pl-v2-layout-card-out md:h-[calc(100vh-4rem)] '
              )}
            >
              <ScrollArea.Viewport
                className={`${MobileBottomCalcVh} w-full md:h-full`}
              >
                <SidebarContent className=" ">
                  <SidebarGroup className="px-0">
                    <SidebarGroupContent className="">
                      {scheduleMeData.data ? (
                        <AgendaWeek locationData={locationData} />
                      ) : null}
                    </SidebarGroupContent>
                  </SidebarGroup>
                </SidebarContent>
              </ScrollArea.Viewport>
              <Scrollbar className="-mr-v2-layout-card-scrollbar" />
            </ScrollArea.Root>
          </div>
          <div
            className={cn(
              'relative flex flex-1 flex-col overflow-hidden',
              `${CardV2LayoutClasses} w-full transition-[width] duration-300 ease-in-out`,
              'ml-0 md:m-v2-layout-card-out'
            )}
          >
            <ColumnWidthProvider
              daysCount={finalDaysCount}
              containerRef={containerRef}
            >
              <Tabs
                defaultValue="attendance"
                value={activeTab}
                onValueChange={handleTabChange}
                className="w-full items-center justify-center"
              >
                <div className="flex w-full flex-col items-center justify-center">
                  <DashboardContentWrapper maxWidth={dashboardWidth}>
                    <div
                      className="flex w-full flex-row items-center justify-between gap-6 md:gap-0"
                      ref={containerRef}
                    >
                      {calendarSwitch ? (
                        <div className={'mt-7 mb-4 '}>{calendarSwitch}</div>
                      ) : null}

                      <TabsList
                        className={cn(
                          'mt-3 w-full justify-center md:mt-5 md:mb-4',
                          'items-center md:items-start',
                          'justify-start'
                        )}
                        showTabsAsDropdownInMobile
                      >
                        <TabsTrigger
                          variant={selectedEvent ? 'default' : 'minimalistic'}
                          className={cn(selectedEvent ? ' mb-2.5 pr-0' : '')}
                          value="attendance"
                          onClick={
                            selectedEvent
                              ? () => setSelectedEvent(null)
                              : undefined
                          }
                        >
                          {selectedEvent ? (
                            <span className="flex items-center gap-2">
                              <ArrowLeftIcon className="size-4" />
                              Back{' '}
                            </span>
                          ) : (
                            'Attendance'
                          )}
                        </TabsTrigger>
                        {!selectedEvent && isAdmin && hasStations && (
                          <TabsTrigger variant="minimalistic" value="visitors">
                            Visitors
                          </TabsTrigger>
                        )}
                        {!selectedEvent && (
                          <TabsTrigger variant="minimalistic" value="news">
                            News
                          </TabsTrigger>
                        )}
                        {!selectedEvent && hasPages && (
                          <TabsTrigger variant="minimalistic" value="siteInfo">
                            Site Info
                          </TabsTrigger>
                        )}
                      </TabsList>
                      {!selectedEvent && (
                        <div className={'mt-3 md:mt-0'}>
                          <SelectOffices
                            // offices={dashboardFilters.offices ?? undefined}
                            onChange={(ids: string[] | string) => {
                              setOffices(Array.isArray(ids) ? ids[0] : ids);
                            }}
                            defaultValue={dashboardFilters.office ?? undefined}
                            disabled={isComponentLoading}
                            showSelectedLabel={isMd}
                            allowClear={false}
                          />
                        </div>
                      )}
                    </div>
                  </DashboardContentWrapper>
                </div>

                <ScrollArea.Root
                  type="hover"
                  scrollHideDelay={500}
                  className={`flex-1 ${MobileBottomCalcVh} md:h-[calc(100vh-10rem)]`}
                >
                  <ScrollArea.Viewport className="[&>div]:!block block h-full w-full">
                    <div className="flex w-full flex-col items-center justify-center">
                      <DashboardContentWrapper maxWidth={dashboardWidth}>
                        <TabsContent value="attendance" asChild>
                          <div>
                            {selectedEvent ? (
                              <DashboardEventDetails
                                schedule={selectedEvent}
                                calendarSwitch={
                                  <div
                                    className={
                                      'flex w-full items-center justify-between gap-2'
                                    }
                                  >
                                    {calendarSwitch}{' '}
                                    <Button
                                      variant={'ghost'}
                                      onClick={() => setSelectedEvent(null)}
                                    >
                                      <CloseIcon className={'size-4'} />
                                      Close Event
                                    </Button>
                                  </div>
                                }
                              />
                            ) : (
                              <TeamSchedule daysCount={finalDaysCount} />
                            )}
                          </div>
                        </TabsContent>

                        <TabsContent value="visitors" asChild>
                          <div>
                            <Visitors />
                          </div>
                        </TabsContent>

                        <TabsContent value="news" asChild>
                          <div>
                            <News />
                          </div>
                        </TabsContent>
                        <TabsContent value="siteInfo" asChild>
                          <div>
                            <SiteInfo />
                          </div>
                        </TabsContent>
                      </DashboardContentWrapper>
                    </div>
                  </ScrollArea.Viewport>
                  <Scrollbar className="z-50 mr-v2-layout-card-scrollbar" />
                </ScrollArea.Root>
              </Tabs>
            </ColumnWidthProvider>
          </div>
        </div>
      </div>

      {oooRequest ? (
        <OOORequest
          onClose={() => {
            setOooRequest(null);
            locationData.invalidateQueries();
          }}
          request={oooRequest}
        />
      ) : null}
      {onConfirmDialogCallback ? (
        <ConfirmModal
          message={
            <p className="text-gray-500 text-sm">
              If you confirm the current reservations on this day will be
              cancelled
            </p>
          }
          onConfirm={onConfirmDialogCallback}
          title={
            <span className="font-bold text-2xl text-parent">
              Do you want to cancel?
            </span>
          }
          onOpenChange={(isOpen) => !isOpen && setOnConfirmDialogCallback(null)}
          isOpen={!!onConfirmDialogCallback}
        />
      ) : null}
    </>
  );
}

const Scrollbar = ({ className }: { className?: string }) => {
  return (
    <ScrollArea.Scrollbar
      orientation="vertical"
      className={cn(
        'z-50 flex w-3 bg-gray-50/50 p-[2px] hover:bg-gray-100',
        className
      )}
    >
      <ScrollArea.Thumb className="relative flex-1 rounded-full bg-gray-400 hover:bg-gray-500" />
    </ScrollArea.Scrollbar>
  );
};
